import {
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  differenceInDays,
} from 'date-fns'
import { useState, useEffect, ReactNode, useRef } from 'react'
import { useTranslation } from 'next-i18next'

const CountdownClock = ({
  countdownTo,
  countdownFinishedMessage,
}: {
  countdownTo: Date | null | ((now: Date) => Date | null)
  countdownFinishedMessage: ReactNode
}) => {
  const { t } = useTranslation('components')
  const [timeLeft, setTimeLeft] = useState<{
    days: number
    hours: number
    minutes: number
    seconds: number
  } | null>(() => calculateTimeLeft(countdownTo))
  const countdownToRef = useRef(countdownTo)

  useEffect(() => {
    countdownToRef.current = countdownTo
  }, [countdownTo])

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(countdownToRef.current)
      setTimeLeft(newTimeLeft)
      if (!newTimeLeft) {
        clearInterval(timer)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  if (timeLeft == null) {
    return countdownFinishedMessage
  }

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center space-x-2">
        <CountdownDigit
          value={timeLeft.days}
          label={t('shared.countdown-clock.days')}
        />
        <CountdownDigit
          value={timeLeft.hours}
          label={t('shared.countdown-clock.hours')}
        />
        <CountdownDigit
          value={timeLeft.minutes}
          label={t('shared.countdown-clock.minutes')}
        />
        <CountdownDigit
          value={timeLeft.seconds}
          label={t('shared.countdown-clock.seconds')}
        />
      </div>
    </div>
  )
}

export default CountdownClock

const CountdownDigit = ({ value, label }: { value: number; label: string }) => {
  return (
    <div className="flex flex-col items-center">
      <div className="flex h-10 w-12 items-center justify-center rounded-lg bg-th-bkg-3 text-xl font-bold tabular-nums text-th-fgd-1">
        {value.toString().padStart(2, '0')}
      </div>
      <span className="mt-1 text-xxs text-th-fgd-4">{label}</span>
    </div>
  )
}

function calculateTimeLeft(
  countdownTo: Date | null | ((now: Date) => Date | null),
): {
  days: number
  hours: number
  minutes: number
  seconds: number
} | null {
  if (!countdownTo) {
    return null
  }

  const now = new Date()
  const countdownToDate =
    typeof countdownTo === 'function' ? countdownTo(now) : countdownTo

  if (!countdownToDate || countdownToDate <= now) {
    return null
  }

  const days = differenceInDays(countdownToDate, now)
  const hours = differenceInHours(countdownToDate, now) % 24
  const minutes = differenceInMinutes(countdownToDate, now) % 60
  const seconds = differenceInSeconds(countdownToDate, now) % 60

  return { days, hours, minutes, seconds }
}
